import { FAN_ADMIN_NAV_ITEM } from "../constants/adminWorkspace";
import {
  ACCOUNT_DESTINATIONS,
  DEFAULT_SPORT,
  DESTINATION_LABELS,
  DESTINATION_PATHS,
  LIVE_DESTINATIONS,
  NAV_GROUPS,
  SPORT_BASE_PATH,
  SPORT_BY_ID,
  SPORT_IDS,
  SPORT_STORAGE_KEY,
  SWITCH_ALIASES,
} from "../constants/sportWorkspace";

const ACCOUNT_MATCHERS = [
  { destination: "admin", prefix: ACCOUNT_DESTINATIONS.admin },
  { destination: "dashboard", prefix: ACCOUNT_DESTINATIONS.dashboard },
  { destination: "profile", prefix: ACCOUNT_DESTINATIONS.profile },
  { destination: "settings", prefix: ACCOUNT_DESTINATIONS.settings },
];

const REST_MATCHERS = [
  { destination: "player", match: (rest) => rest.startsWith("/player/") },
  {
    destination: "players",
    match: (rest) => rest === "/players" || rest.startsWith("/players/"),
  },
  {
    destination: "compare",
    match: (rest) => rest === "/compare" || rest.startsWith("/compare/"),
  },
  { destination: "team", match: (rest) => rest.startsWith("/teams/") },
  { destination: "teams", match: (rest) => rest === "/teams" },
  {
    destination: "queryBuilder",
    match: (rest) =>
      rest === "/queryBuilder" || rest.startsWith("/queryBuilder/"),
  },
  {
    destination: "allTimeGreats",
    match: (rest) =>
      rest === "/all-time-greats" || rest.startsWith("/all-time-greats/"),
  },
  {
    destination: "trivia",
    match: (rest) => rest === "/trivia" || rest.startsWith("/trivia/"),
  },
  {
    destination: "community",
    match: (rest) => rest === "/community" || rest.startsWith("/community/"),
  },
  {
    destination: "lineup",
    match: (rest) => rest === "/lineup" || rest.startsWith("/lineup/"),
  },
  { destination: "game", match: (rest) => rest.startsWith("/game/") },
  { destination: "home", match: (rest) => rest === "/" || rest === "" },
];

const PREFIXED_SPORTS = SPORT_IDS.filter(
  (sport) => SPORT_BASE_PATH[sport] !== "/app"
);

const liveBySport = Object.fromEntries(
  Object.entries(LIVE_DESTINATIONS).map(([sport, destinations]) => [
    sport,
    new Set(destinations),
  ])
);

export const isAccountPath = (pathname) =>
  ACCOUNT_MATCHERS.some(
    ({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

export const getSportFromPath = (pathname) => {
  if (isAccountPath(pathname)) return null;

  const prefixed = PREFIXED_SPORTS.find((sport) => {
    const base = SPORT_BASE_PATH[sport];
    return pathname === base || pathname.startsWith(`${base}/`);
  });
  if (prefixed) return prefixed;

  if (pathname === "/app" || pathname.startsWith("/app/")) return DEFAULT_SPORT;
  return null;
};

const getPathRest = (pathname, sport) => {
  if (!sport) return pathname;
  const base = SPORT_BASE_PATH[sport];
  if (pathname === base) return "/";
  return pathname.slice(base.length) || "/";
};

export const getDestinationFromPath = (pathname) => {
  const account = ACCOUNT_MATCHERS.find(
    ({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
  if (account) return account.destination;

  const sport = getSportFromPath(pathname) || DEFAULT_SPORT;
  const rest = getPathRest(pathname, sport);
  const matched = REST_MATCHERS.find(({ match }) => match(rest));
  return matched?.destination || "home";
};

export const getSportPath = (sport, destination) => {
  if (ACCOUNT_DESTINATIONS[destination]) {
    return ACCOUNT_DESTINATIONS[destination];
  }

  const base = SPORT_BASE_PATH[sport] || SPORT_BASE_PATH[DEFAULT_SPORT];
  const segment = DESTINATION_PATHS[destination];
  if (segment == null || destination === "home") return base;
  return `${base}${segment}`;
};

export const switchSportPath = (pathname, nextSport) => {
  const destination = getDestinationFromPath(pathname);
  if (ACCOUNT_DESTINATIONS[destination]) return pathname;

  const analog = SWITCH_ALIASES[destination] || destination;
  const target =
    DESTINATION_PATHS[analog] != null || analog === "home" ? analog : "home";
  return getSportPath(nextSport, target);
};

export const isDestinationLive = (sport, destination) => {
  if (ACCOUNT_DESTINATIONS[destination]) return true;
  return liveBySport[sport]?.has(destination) === true;
};

export const readStoredSport = (storage) => {
  const value = storage?.getItem?.(SPORT_STORAGE_KEY);
  return SPORT_BY_ID[value] ? value : DEFAULT_SPORT;
};

export const writeStoredSport = (sport, storage) => {
  if (!SPORT_BY_ID[sport] || !storage?.setItem) return;
  storage.setItem(SPORT_STORAGE_KEY, sport);
};

export const isNavItemActive = (itemId, destination) =>
  itemId === destination || SWITCH_ALIASES[destination] === itemId;

export const getComingSoonPaths = () =>
  SPORT_IDS.flatMap((sport) =>
    Object.keys(DESTINATION_PATHS)
      .filter(
        (destination) =>
          destination !== "home" && !isDestinationLive(sport, destination)
      )
      .map((destination) => getSportPath(sport, destination))
  );

export const getWorkspaceNavGroups = (sport, isAdmin = false) => {
  const groups = NAV_GROUPS.map((group) => ({
    id: group.id,
    label: group.label,
    items: group.destinations.map((destination) => ({
      id: destination,
      text: DESTINATION_LABELS[destination],
      path: getSportPath(sport, destination),
      comingSoon: !isDestinationLive(sport, destination),
    })),
  }));

  if (isAdmin) {
    groups.push({
      id: "admin",
      label: "Admin",
      items: [
        {
          ...FAN_ADMIN_NAV_ITEM,
          comingSoon: false,
          isAdmin: true,
        },
      ],
    });
  }

  return groups;
};
